import { ReactPortal } from '../../../components/reactPortal'
import { Modal } from '../../../components/modal/modal'
import { CustomButton } from '../../../components/customButton'
import { FC, memo, useEffect, useMemo, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { IVoice } from '../../../@types/tts'
import { useDispatch, useSelector } from '../../../hooks/redux'
import classes from '../../../styles/tts/ttsModal.module.scss'
import modalClasses from '../../../styles/mobilePopup.module.scss'
import { MobilePopup } from '../../../components/mobilePopup'
import { TtsModalFilters } from '../ttsFilters/ttsModalFilters'
import { ModalVoices } from './ttsModalVoices'
import { ttsUpdatePickedVoices } from '../../../store/slices/tts/ttsSlice'

export const TtsModal: FC<{
  showModal: boolean
  handleClose: () => void
  prevVoices: IVoice[]
  confirmChanges: () => void
}> = memo(({ handleClose, showModal, prevVoices, confirmChanges }) => {
  const dispatch = useDispatch()
  const voices = useSelector((state) => state.tts.voices)
  const ttsFilters = useSelector((state) => state.tts.filters)
  const pickedVoices = voices.filter((item) => item.picked).length
  const pickedPrevVoices = prevVoices.filter((item) => item.picked).length
  const [baselinePickedCount, setBaselinePickedCount] = useState<number>(pickedPrevVoices)

  useEffect(() => {
    if (showModal) {
      setBaselinePickedCount(pickedPrevVoices)
    }
  }, [showModal, pickedPrevVoices])

  const qtyNewVoices = pickedVoices - baselinePickedCount
  const isLaptopOrMobile = useMediaQuery({ query: '(max-width: 768px)' })

  const filteredVoices = useMemo(() => {
    let arr = voices

    if (ttsFilters?.sex?.length) {
      arr = arr.filter((item) => ttsFilters.sex.includes(item.sex) || item?.sex === '')
    }

    if (ttsFilters?.language?.length) {
      arr = arr.filter((item) => item.languages.length === 0 || ttsFilters.language.includes(item.languages[0]))
    }

    return arr
  }, [voices, ttsFilters])

  const allFilteredPicked = useMemo(
    () => filteredVoices.length > 0 && filteredVoices.every((v) => v.picked),
    [filteredVoices]
  )

  const handleTogglePickAll = () => {
    if (!filteredVoices.length) return
    const filteredNames = new Set(filteredVoices.map((v) => v.name))
    const newVoices = voices.map((v) => (filteredNames.has(v.name) ? { ...v, picked: !allFilteredPicked } : v))

    if (allFilteredPicked) {
      const newPickedCount = newVoices.filter((v) => v.picked).length
      setBaselinePickedCount(newPickedCount)
    }
    dispatch(ttsUpdatePickedVoices(newVoices))
  }

  const pickedAllBtnText = allFilteredPicked ? 'Снять все' : 'Выбрать все'
  return !isLaptopOrMobile ? (
    <ReactPortal>
      <Modal
        classes={{ modalContent: classes.ttsModal }}
        title="Выбор голосов"
        subtitle="Вы можете прослушать наших дикторов и выбрать несколько голосов сразу"
        isOpen={showModal}
        handleClose={!isLaptopOrMobile ? handleClose : confirmChanges}
      >
        <div className={classes.ttsModalContent}>
          <TtsModalFilters />
          <ModalVoices />
          <div className={classes.ttsModalButtonGroup}>
            <CustomButton
              onClick={handleTogglePickAll}
              viewType="outline"
              className={`${classes.ttsModalButtonLeft} ${classes.ttsModalPickAllBtn}`}
            >
              {pickedAllBtnText}
            </CustomButton>
            <CustomButton onClick={handleClose}>Отмена</CustomButton>
            <CustomButton onClick={confirmChanges} viewType="primary" style={{ width: 'auto', maxWidth: 'none' }}>
              Добавить голоса {qtyNewVoices > 0 ? `(${qtyNewVoices})` : ''}
            </CustomButton>
          </div>
        </div>
      </Modal>
    </ReactPortal>
  ) : (
    <MobilePopup show={showModal} handleClose={handleClose}>
      <div className={modalClasses.modalPopupTitleWrapper}>
        <div className={modalClasses.mobilePopupTitle}>Выбор голосов</div>
        <button onClick={handleTogglePickAll} className={modalClasses.mobilePopupCloseBtn}>
          {pickedAllBtnText}
        </button>
        <button onClick={confirmChanges} className={modalClasses.mobilePopupCloseBtn}>
          Добавить голоса
        </button>
      </div>
      <div className={classes.ttsModalContent}>
        <TtsModalFilters />
        <ModalVoices />
      </div>
    </MobilePopup>
  )
})

TtsModal.displayName = 'TtsModalVoices'
